/** 점수 탭 — an app's market score (S3, `/apps/:id/score`).
 *
 *  Four numbers on a 0–10 scale with a bar each, the recommendation, and the
 *  market / competition / entry-risk paragraphs. "다시 계산" recomputes from the
 *  stored keywords and competitors on the server; nothing leaves the service
 *  (`scoring.Service.Recompute` reads the repositories and writes the result). */

import { Calculator, Gauge } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Analysis } from '@/api/client'
import { qk, useAnalysis } from '@/lib/queries'
import { scoreBand, statusOf, TONE_CLASS } from '@/lib/status'
import { formatFull, formatAbsolute } from '@/lib/time'
import { Button, EmptyState, errorText, ErrorState, ScoreNumber, Skeleton, useToast } from '@/ui'

/** Competition and entry risk read the other way: a high number is the bad
 *  news, so their band is taken from 10 − value (a 8.4 risk is "나쁨", not a
 *  green "좋음"). */
const SCORES: { key: keyof Analysis; label: string; lowerIsBetter?: boolean }[] = [
  { key: 'overall_score', label: '종합' },
  { key: 'market_score', label: '시장' },
  { key: 'competition_score', label: '경쟁', lowerIsBetter: true },
  { key: 'entry_risk_score', label: '진입 위험', lowerIsBetter: true },
]

const REPORTS: { key: keyof Analysis; label: string }[] = [
  { key: 'market_report', label: '시장' },
  { key: 'competition_report', label: '경쟁' },
  { key: 'entry_risk_report', label: '진입' },
]

function ScoreCard({ label, value, lowerIsBetter }: { label: string; value: number; lowerIsBetter?: boolean }) {
  const def = statusOf('score', scoreBand(lowerIsBetter ? 10 - value : value))
  const pct = Math.max(0, Math.min(100, (value / 10) * 100))
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2 text-meta text-muted-foreground">
        <span>{label}</span>
        {lowerIsBetter && <span>낮을수록 좋다</span>}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <ScoreNumber value={value} size="lg" lowerIsBetter={lowerIsBetter} />
        <span className="text-meta text-muted-foreground">/ 10</span>
        <span className="ml-auto text-meta text-muted-foreground">{def.label}</span>
      </div>
      <div
        role="img"
        aria-label={`${label} ${value.toFixed(1)} / 10`}
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div className={`h-full rounded-full ${TONE_CLASS[def.tone].dot}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export function ScoreTab({ appId }: { appId: number }) {
  const qc = useQueryClient()
  const toast = useToast()
  const analysis = useAnalysis(appId)
  const recompute = useMutation({
    mutationFn: () => api.recomputeAnalysis(appId),
    onSuccess: () => {
      toast.success('점수를 다시 계산했다')
      qc.invalidateQueries({ queryKey: qk.analysis(appId) })
      qc.invalidateQueries({ queryKey: ['analyses'] })
      qc.invalidateQueries({ queryKey: qk.dashboard })
      qc.invalidateQueries({ queryKey: qk.trackingOverview })
    },
    onError: (e) => toast.error(errorText(e)),
  })
  const a = analysis.data

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="max-w-2xl text-table text-muted-foreground">
          키워드와 경쟁사로 시장·경쟁·진입 위험을 0~10점으로 매긴다. 서비스 안에서만 계산한다.
        </p>
        <Button variant="primary" icon={Calculator} loading={recompute.isPending} onClick={() => recompute.mutate()}>
          {recompute.isPending ? '계산 중' : a ? '다시 계산' : '점수 계산'}
        </Button>
      </div>

      {analysis.error ? (
        <ErrorState title="점수를 불러오지 못했다" error={analysis.error} onRetry={() => analysis.refetch()} />
      ) : analysis.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !a ? (
        <EmptyState
          icon={Gauge}
          title="아직 점수가 없다"
          description="경쟁사를 추가하고 키워드를 분석한 뒤 점수 계산을 누른다."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SCORES.map((s) => (
              <ScoreCard key={s.key} label={s.label} value={a[s.key] as number} lowerIsBetter={s.lowerIsBetter} />
            ))}
          </div>

          <section className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-section font-semibold">추천</h2>
            <p className="mt-1.5 text-body">{a.recommendation}</p>
          </section>

          <section className="space-y-3 rounded-lg border border-border bg-card p-4" aria-label="보고">
            {REPORTS.map((r) => (
              <p key={r.key} className="text-body">
                <span className="font-semibold">{r.label}</span>{' '}
                {a[r.key] as string}
              </p>
            ))}
          </section>

          <p className="text-meta text-muted-foreground" title={formatFull(a.updated_at)}>
            갱신 {formatAbsolute(a.updated_at)}
          </p>
        </>
      )}
    </div>
  )
}
